import { TResourceGroupItem } from '@/console/features/groups/components/groups.types';
import { useUpdateDeviceMutation } from '@/shared/redux/api/assets/devices/devices-api';
import { useUpdateDocumentMutation } from '@/shared/redux/api/assets/documents/documents-api';
import { useUpdateGatewayMutation } from '@/shared/redux/api/assets/gateways/gateways-api';
import { useUpdateResourceGroupMutation } from '@/shared/redux/api/assets/groups/groups-api';
import { useUpdateLicenseMutation } from '@/shared/redux/api/assets/licenses/licenses-api';
import { useUpdateProjectMutation } from '@/shared/redux/api/assets/projects/projects-api';
import { useUpdateVaultMutation } from '@/shared/redux/api/assets/vaults/vaults-api';
import {
  useUpdateUserMutation,
  useUpdateUserRoleMutation,
} from '@/shared/redux/api/ident/ident-api';
import { useUpdateIntegrationMutation } from '@/shared/redux/api/integration/integrations-api';
import { useUpdatePipelineConfigurationMutation } from '@/shared/redux/api/jobs/jobs-api';
import {
  UpdateDeviceMutation,
  UpdateDocumentMutation,
  UpdateGatewayMutation,
  UpdateIntegrationMutation,
  UpdateLicenseMutation,
  UpdatePipelineConfigurationMutation,
  UpdateProjectMutation,
  UpdateResourceGroupMutation,
  UpdateUserMutation,
  UpdateUserRoleMutation,
  UpdateVaultMutation,
} from '@/shared/types/types.generated';

export const useGroupResourceUpdatePromises = () => {
  const [updateProject] = useUpdateProjectMutation();
  const [updateDevice] = useUpdateDeviceMutation();
  const [updateGateway] = useUpdateGatewayMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [updateVault] = useUpdateVaultMutation();
  const [updateLicense] = useUpdateLicenseMutation();
  const [updateGroup] = useUpdateResourceGroupMutation();
  const [updateUser] = useUpdateUserMutation();
  const [updateRole] = useUpdateUserRoleMutation();
  const [updatePipeline] = useUpdatePipelineConfigurationMutation();
  const [updateIntegration] = useUpdateIntegrationMutation();

  const createResourceUpdatePromises = (
    resources: TResourceGroupItem[],
    groupId: string,
  ) => {
    const promises: Promise<
      | UpdateDeviceMutation
      | UpdateGatewayMutation
      | UpdateProjectMutation
      | UpdateDocumentMutation
      | UpdateVaultMutation
      | UpdateLicenseMutation
      | UpdateResourceGroupMutation
      | UpdateUserMutation
      | UpdateUserRoleMutation
      | UpdatePipelineConfigurationMutation
      | UpdateIntegrationMutation
    >[] = [];

    resources.forEach((item) => {
      if (item.__typename === 'DeviceResponseType') {
        promises.push(
          updateDevice({
            deviceId: item.deviceId,
            device: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'GatewayResponseType') {
        promises.push(
          updateGateway({
            gatewayId: item.gatewayId,
            gateway: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'ProjectResponseType') {
        promises.push(
          updateProject({
            projectId: item.projectId,
            project: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
        return;
      }

      if (item.__typename === 'DocumentResponseType') {
        promises.push(
          updateDocument({
            documentId: item.documentId,
            document: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }

      if (item.__typename === 'VaultResponseType') {
        promises.push(
          updateVault({
            vaultId: item.vaultId,
            vault: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }

      if (item.__typename === 'LicenseResponseType') {
        promises.push(
          updateLicense({
            licenseId: item.licenseId,
            license: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }

      if (item.__typename === 'ResourceGroupResponseType') {
        promises.push(
          updateGroup({
            groupId: item.groupId,
            resourceGroup: {
              parentGroupId: groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }

      if (item.__typename === 'UserResponseType') {
        promises.push(
          updateUser({
            userId: item.userId,
            user: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }

      if (item.__typename === 'UserRoleResponseType') {
        promises.push(
          updateRole({
            userRoleId: item.userRoleId,
            userRole: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }

      if (item.__typename === 'PipelineConfigurationResponseType') {
        promises.push(
          updatePipeline({
            pipelineConfigurationId: item.pipelineConfigurationId,
            input: {
              groupId,
              trigger: item.trigger,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }

      if (item.__typename === 'IntegrationResponseType') {
        promises.push(
          updateIntegration({
            integrationId: item.integrationId,
            integration: {
              groupId,
              objectVersion: item.objectVersion! + 1,
            },
          }).unwrap(),
        );
      }
    });

    return promises;
  };

  return { createResourceUpdatePromises };
};
